import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, imgUrl, typeLabel, allPhotos, ROMAN } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Lightbox } from "@/components/site/Lightbox";
import { Reveal } from "@/components/site/Reveal";
import { NarrativeGallery } from "@/components/site/NarrativeGallery";
import { Img } from "@/components/blocks/Parts";

const Meta = ({ g, count }) => (
  <dl className="grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-pietra pt-6 text-sm">
    {[["Protagonisti", g.title], ["Luogo", g.location], ["Data o stagione", g.season], ["Fotografie", count]].filter(([, v]) => v).map(([k, v]) => (
      <div key={k}><dt className="eyebrow">{k}</dt><dd className="mt-2 text-ink">{v}</dd></div>
    ))}
  </dl>
);

export default function GalleryDetail() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [next, setNext] = useState(null);
  const [err, setErr] = useState(false);
  const [idx, setIdx] = useState(null);
  useSeo(g?.title, g?.intro);

  useEffect(() => {
    setG(null); setErr(false);
    api.get(`/public/galleries/${slug}`).then((r) => setG(r.data)).catch(() => setErr(true));
    api.get("/public/galleries").then((r) => {
      const i = r.data.findIndex((x) => x.slug === slug);
      setNext(r.data.length > 1 ? r.data[(i + 1) % r.data.length] : null);
    });
  }, [slug]);

  if (err) return (
    <div className="pt-48 pb-32 container-x" data-testid="gallery-not-found">
      <p className="font-serif text-5xl font-light">Storia non trovata.</p>
      <Link to="/portfolio" className="btn-line mt-10">Torna al portfolio</Link>
    </div>
  );
  if (!g) return <div className="h-screen" />;
  const photos = allPhotos(g);
  let offset = 0;

  return (
    <article data-testid="gallery-detail-page">
      <header className="container-x pt-36 sm:pt-48 pb-12">
        <Link to="/portfolio" className="inline-flex items-center gap-2 text-[13px] text-mute link-u" data-testid="gallery-back-link"><ArrowLeft size={14} strokeWidth={1.4} /> Portfolio</Link>
        <Reveal>
          <p className="eyebrow mt-12">{typeLabel(g.category)}</p>
          <h1 className="mt-6 font-serif font-light text-[54px] sm:text-[88px] lg:text-[120px] leading-[0.95] tracking-[-0.03em]" data-testid="gallery-title">{g.title}</h1>
        </Reveal>
        <Reveal delay={0.2} className="mt-14"><Meta g={g} count={photos.length} /></Reveal>
      </header>
      <Reveal className="px-0 sm:px-10 lg:px-16"><Img src={g.cover} alt={g.title} eager className="w-full max-h-[88vh] object-cover" /></Reveal>
      {g.intro && (
        <section className="container-x py-24 sm:py-36 grid grid-cols-12">
          <Reveal className="col-span-12 lg:col-span-7 lg:col-start-4">
            <p className="font-serif font-light text-[26px] sm:text-[36px] leading-[1.35] tracking-[-0.01em]" data-testid="gallery-intro">{g.intro}</p>
          </Reveal>
        </section>
      )}
      <div className={`container-x pb-24 space-y-28 sm:space-y-40 ${g.intro ? "" : "pt-28 sm:pt-40"}`}>
        {g.chapters.map((c, k) => {
          const start = offset;
          offset += c.photos.length;
          return (
            <section key={c.id} data-testid={`chapter-${k}`}>
              <Reveal className="grid grid-cols-12 gap-6 mb-14 sm:mb-20">
                <p className="col-span-2 font-serif italic text-2xl text-sagedark">{ROMAN[k]}</p>
                <div className="col-span-10 lg:col-span-6">
                  <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-[-0.02em]">{c.title}</h2>
                  {c.text && <p className="mt-5 text-[15px] text-stoneish leading-[1.8] max-w-lg">{c.text}</p>}
                </div>
              </Reveal>
              <NarrativeGallery photos={c.photos} offset={start} onOpen={setIdx} />
            </section>
          );
        })}
      </div>
      <section className="border-t border-pietra">
        <div className="container-x py-20 grid sm:grid-cols-2 gap-10 items-end">
          <div>
            <p className="eyebrow mb-4">Vi riconoscete in questo modo di raccontare?</p>
            <Link to="/contatti" className="btn-ink" data-testid="gallery-cta-btn">Scriveteci <ArrowRight size={14} /></Link>
          </div>
          {next && next.slug !== g.slug && (
            <Link to={`/portfolio/${next.slug}`} className="sm:text-right group" data-testid="gallery-next-link">
              <p className="eyebrow">Storia successiva</p>
              <p className="mt-3 font-serif font-light text-4xl sm:text-5xl tracking-[-0.02em] group-hover:text-sagedark transition-colors duration-700">{next.title} →</p>
            </Link>
          )}
        </div>
      </section>
      <Lightbox photos={photos} index={idx} onClose={() => setIdx(null)} onIndex={setIdx} />
    </article>
  );
}
